import axios from 'axios';
import React, { useEffect, useState } from 'react';
import '../../styles/org.css';
import { toast } from 'react-toastify';

const OrgChart = () => {
  const [employees, setEmployees] = useState([]);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const storedToken = localStorage.getItem("token");
  
  // Function to get logged-in user's email
  const getLoggedInUserEmail = () => {
    try {
      const userDetailsString = localStorage.getItem("userDetails");
      if (userDetailsString) {
        const userDetails = JSON.parse(userDetailsString);
        return userDetails.OfficialEmail;
      }
      return null;
    } catch (error) {
      console.error("Error parsing userDetails from localStorage:", error);
      return null;
    }
  };

  // Add debugging at component level
  console.log("Component loaded - userDetails from localStorage:", localStorage.getItem("userDetails"));
  console.log("Component loaded - parsed OfficialEmail:", getLoggedInUserEmail());

  useEffect(() => {
    const fetchEmployees = async () => {
      console.log("fetchEmployees function started");
      
      setLoading(true);
      try {
        const path = "/getEmployeeMasterReport";
        const URL = process.env.REACT_APP_BE_URL + path;
        console.log("Making API call to:", URL);
        
        const response = await axios.get(URL, {
          headers: {
            Authorization: `Bearer ${storedToken}`,
          },
        });
        
        console.log("API Response received:", response.data);
        
        const data = response.data;
        const employeeList = data.onboardEmployeeDetails || [];
        
        console.log("Employee list length:", employeeList.length);
        console.log("First employee:", employeeList[0]);
        
        setEmployees(employeeList);

        // Get logged-in user's email from localStorage
        const loggedInUserEmail = getLoggedInUserEmail();
        console.log("loggedInUserEmail from parsed userDetails:", loggedInUserEmail);
        
        if (loggedInUserEmail) {
          // Find the logged-in user in the employee list
          const loggedInUser = employeeList.find(emp => 
            emp.OfficialEmail.toLowerCase() === loggedInUserEmail.toLowerCase()
          );
          
          console.log("Found logged-in user:", loggedInUser);
          
          if (loggedInUser) {
            setCurrentEmployee(loggedInUser);
            console.log("Set current employee to logged-in user:", loggedInUser);
          } else {
            console.warn("Logged-in user not found in employee list");
            console.log("Looking for email:", loggedInUserEmail);
            console.log("Available emails in employee list:", employeeList.map(emp => emp.OfficialEmail));
            // Fallback to first employee if logged-in user not found
            setCurrentEmployee(employeeList[0] || null);
          }
        } else {
          console.warn("No logged-in user email found in localStorage");
          // Fallback to first employee
          setCurrentEmployee(employeeList[0] || null);
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching employee data:", err);
        console.error("Error details:", err.response?.data || err.message);
        setError("Failed to load organization data. Please try again later.");
        toast.error("Failed to load organization data");
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [storedToken]);

  useEffect(() => {
    // Filter employees based on search term
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      return;
    }

    const term = searchTerm.toLowerCase();
    const results = employees.filter(emp => 
      emp.FirstName.toLowerCase().includes(term) || 
      emp.LastName.toLowerCase().includes(term) || 
      emp.Designation.toLowerCase().includes(term) || 
      emp.Department.toLowerCase().includes(term) ||
      emp.OfficialEmail.toLowerCase().includes(term)
    );

    setSearchResults(results);
  }, [searchTerm, employees]);

  const findManager = (employee) => {
    return employees.find(emp => emp.OfficialEmail === employee.ManagerEmail);
  };

  const findReportees = (employee) => {
    return employees.filter(emp => emp.ManagerEmail === employee.OfficialEmail);
  };

  const handleEmployeeClick = (employee) => {
    setCurrentEmployee(employee);
    setSearchTerm('');
    setShowSearchResults(false);
  };

  // Modified handleBackToTop to show logged-in person instead of top-level employee
  const handleBackToTop = () => {
    const loggedInUserEmail = getLoggedInUserEmail();
    console.log("Back to Top clicked - loggedInUserEmail:", loggedInUserEmail);
    
    if (loggedInUserEmail) {
      const loggedInUser = employees.find(emp => 
        emp.OfficialEmail.toLowerCase() === loggedInUserEmail.toLowerCase()
      );
      if (loggedInUser) {
        setCurrentEmployee(loggedInUser);
        console.log("Set current employee back to logged-in user:", loggedInUser);
      } else {
        console.warn("Logged-in user not found when trying to go back to top");
      }
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowSearchResults(true);
  };

  const handleSearchFocus = () => {
    if (searchTerm.trim() !== '') {
      setShowSearchResults(true);
    }
  };

  const handleClickOutside = () => {
    setTimeout(() => {
      setShowSearchResults(false);
    }, 200);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading organization chart...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button className="retry-button" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  if (!currentEmployee) {
    return <p>No employee data available.</p>;
  }

  const manager = findManager(currentEmployee);
  const reportees = findReportees(currentEmployee);

  return (
    <div className="content-page">
      <div className="container-fluid">
        <div className="org-chart-header">
          <h2>Organization Chart</h2>
          <div className="search-container">
            <div className="search-input-wrapper">
              <input
                type="text"
                className="search-input"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onBlur={handleClickOutside}
              />
              {/* <i className="search-icon">🔍</i> */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map(employee => (
                    <div 
                      key={employee.OfficialEmail} 
                      className="search-result-item"
                      onClick={() => handleEmployeeClick(employee)}
                    >
                      <div className="search-result-name">
                        {employee.FirstName} {employee.LastName}
                      </div>
                      <div className="search-result-details">
                        {employee.Designation} • {employee.Department}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {showSearchResults && searchTerm.trim() !== '' && searchResults.length === 0 && (
                <div className="search-results">
                  <div className="no-results">No employees found</div>
                </div>
              )}
            </div>
            <button className="back-to-top-btn" onClick={handleBackToTop}>
              Back to Top
            </button>
          </div>
        </div>
        
        <div className="breadcrumb-nav">
          {manager && (
            <span onClick={() => handleEmployeeClick(manager)} className="breadcrumb-item">
              {manager.FirstName} {manager.LastName}
            </span>
          )}
          <span className="breadcrumb-item">
            {currentEmployee.FirstName} {currentEmployee.LastName}
          </span>
        </div>

        <div className="org-chart-container">
          {manager && (
            <div className="manager-section">
              <h3 className="section-title">Manager</h3>
              <div className="manager-card-wrapper">
                <EmployeeCard employee={manager} onClick={() => handleEmployeeClick(manager)} isManager={true} />
              </div>
            </div>
          )}

          <div className="current-employee-section">
            <h3 className="section-title">Current Selection</h3>
            <div className="current-card-wrapper">
              <EmployeeCard employee={currentEmployee} isCurrent={true} />
            </div>
          </div>

          {reportees.length > 0 && (
            <div className="reportees-section">
              <h3 className="section-title">Direct Reports ({reportees.length})</h3>
              <div className="reportees-grid">
                {reportees.map(reportee => (
                  <EmployeeCard 
                    key={reportee.OfficialEmail} 
                    employee={reportee} 
                    onClick={() => handleEmployeeClick(reportee)} 
                  />
                ))}
              </div>
            </div>
          )}

          {reportees.length === 0 && (
            <div className="no-reportees">
              <p>No direct reports found for this employee.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EmployeeCard = ({ employee, onClick, isManager = false, isCurrent = false }) => {
  const getInitials = (firstName, lastName) => {
    const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
    return firstInitial + lastInitial;
  };

  const cardClass = `employee-card-compact ${isManager ? 'manager-card' : ''} ${isCurrent ? 'current-card' : ''} ${onClick ? 'clickable' : ''}`;

  return (
    <div className={cardClass} onClick={onClick}>
      <div className="card-content">
        <div className="profile-section">
          <div className="profile-picture-compact">
            {employee.ProfilePicture ? (
              <img src={employee.ProfilePicture} alt={`${employee.FirstName} ${employee.LastName}`} />
            ) : (
              <div className="initials-compact">
                {getInitials(employee.FirstName, employee.LastName)}
              </div>
            )}
          </div>
        </div>
        <div className="info-section">
          <div className="employee-name-compact">
            {employee.FirstName} {employee.LastName}
          </div>
          <div className="employee-title-compact">
            {employee.Designation}
          </div>
          <div className="employee-department-compact">
            {employee.Department}
          </div>
        </div>
      </div>
      {onClick && (
        <div className="expand-icon">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M4 6L8 6M6 4L6 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </div>
      )}
      <div className={`status-dot ${employee.Status ? employee.Status.toLowerCase() : 'active'}`}></div>
    </div>
  );
};

export default OrgChart;